/**
 * Own Horse Selector Component
 * Allows players to select their own horses when signing up for events
 * Reuses HorseIcon for display
 */
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Slider,
  Box,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Horse } from "./types";
import HorseIcon from "./HorseIcon";

interface HorseSelection {
  horseId: string;
  horseName: string;
  chukkers: number;
}

interface OwnHorseSelectorProps {
  devPort: string;
  clubId: number;
  playerId: string | number;
  eventType: "games" | "lessons" | "tournaments";
  maxChukkers?: number; // Total chukkers player is signing up for
  onSelectionChange: (selections: HorseSelection[]) => void;
  disabled?: boolean;
}

function OwnHorseSelector({
  devPort,
  clubId,
  playerId,
  eventType,
  maxChukkers = 1,
  onSelectionChange,
  disabled = false,
}: OwnHorseSelectorProps) {
  const [myHorses, setMyHorses] = useState<Horse[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [selections, setSelections] = useState<Map<string, number>>(new Map());

  const isLesson = eventType === "lessons";

  // Load player's horses (user-level, reusable across all clubs)
  const loadMyHorses = useCallback(() => {
    if (!playerId || playerId === -1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    // Fetch from user-level endpoint - horses are tied to the user, not the club
    fetch(`${devPort}horses/user/${playerId}`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          // Filter to only available horses
          const availableHorses = data.filter(
            (h: Horse) => h.status === "available"
          );
          setMyHorses(availableHorses);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [devPort, playerId]);

  useEffect(() => {
    loadMyHorses();
  }, [loadMyHorses]);

  // Calculate total assigned chukkers
  const totalAssignedChukkers = Array.from(selections.values()).reduce(
    (sum, val) => sum + val,
    0
  );
  const remainingChukkers = maxChukkers - totalAssignedChukkers;

  // Update parent when selections change
  useEffect(() => {
    const selectionArray: HorseSelection[] = [];
    selections.forEach((chukkers, horseId) => {
      if (chukkers > 0) {
        const horse = myHorses.find((h) => h.id === horseId);
        if (horse) {
          selectionArray.push({
            horseId,
            horseName: horse.name,
            chukkers,
          });
        }
      }
    });
    onSelectionChange(selectionArray);
  }, [selections, myHorses, onSelectionChange]);

  const handleHorseToggle = (horseId: string, checked: boolean) => {
    const newSelections = new Map(selections);
    if (checked) {
      // For lessons, always assign 1
      // For games/tournaments, default to 1
      newSelections.set(horseId, 1);
    } else {
      newSelections.delete(horseId);
    }
    setSelections(newSelections);
  };

  const handleChukkerChange = (horseId: string, chukkers: number) => {
    const newSelections = new Map(selections);
    if (chukkers <= 0) {
      newSelections.delete(horseId);
    } else {
      newSelections.set(horseId, chukkers);
    }
    setSelections(newSelections);
  };

  // Don't render if no horses
  if (loading) {
    return <LinearProgress />;
  }

  if (myHorses.length === 0) {
    return null; // Player has no horses, don't show anything
  }

  const hasSelections = selections.size > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        bgcolor: hasSelections ? "primary.light" : "background.paper",
        borderColor: hasSelections ? "primary.main" : "divider",
        borderWidth: hasSelections ? 2 : 1,
        transition: "all 0.2s ease-in-out",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: "pointer" }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: hasSelections ? "primary.main" : "grey.200",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                }}
              >
                🐴
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: hasSelections ? "white" : "inherit" }}
                >
                  Use My Own Horses
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: hasSelections ? "rgba(255,255,255,0.85)" : "text.secondary",
                  }}
                >
                  {myHorses.length} horse{myHorses.length !== 1 ? "s" : ""} available
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {!isLesson && totalAssignedChukkers > 0 && (
                <Chip
                  label={`${totalAssignedChukkers}/${maxChukkers} chukkers`}
                  size="small"
                  sx={hasSelections ? { bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' } : {}}
                  color={!hasSelections ? (totalAssignedChukkers >= maxChukkers ? "success" : "warning") : undefined}
                />
              )}
              <IconButton size="small" sx={{ color: hasSelections ? "white" : "inherit" }}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={expanded}>
            <Stack spacing={2}>
              {/* Chukker count warning */}
              {!isLesson && totalAssignedChukkers > maxChukkers && (
                <Alert 
                  severity="error" 
                  variant="outlined"
                  sx={hasSelections ? { 
                    bgcolor: 'rgba(255,255,255,0.15)', 
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    '& .MuiAlert-icon': { color: 'white' }
                  } : {}}
                >
                  You've assigned {totalAssignedChukkers} chukkers but only signed up
                  for {maxChukkers}. Please reduce assignments.
                </Alert>
              )}

              {/* Horse list */}
              {myHorses.map((horse) => {
                const isSelected = selections.has(horse.id);
                const currentChukkers = selections.get(horse.id) || 0;
                // Max for this horse is min of (horse capacity, remaining + current)
                const maxForHorse = isLesson
                  ? 1
                  : Math.min(horse.game_chukkers, remainingChukkers + currentChukkers);

                  return (
                    <Card
                      key={horse.id}
                      variant="outlined"
                      sx={{
                        bgcolor: isSelected ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.9)",
                        borderColor: isSelected ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.5)",
                      }}
                    >
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Stack spacing={1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) =>
                                  handleHorseToggle(horse.id, e.target.checked)
                                }
                                disabled={!isSelected && remainingChukkers <= 0}
                                sx={isSelected ? { 
                                  color: 'white', 
                                  '&.Mui-checked': { color: 'white' } 
                                } : {}}
                              />
                            }
                            label={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <HorseIcon color={horse.color} size={32} />
                                <Box>
                                  <Typography 
                                    variant="subtitle2"
                                    sx={{ color: isSelected ? 'white' : 'inherit' }}
                                  >
                                    {horse.name}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ color: isSelected ? 'rgba(255,255,255,0.85)' : 'text.secondary' }}
                                  >
                                    {horse.sex === "mare"
                                      ? "♀ Mare"
                                      : horse.sex === "stallion"
                                      ? "♂ Stallion"
                                      : "♂ Gelding"} •
                                    Max {horse.game_chukkers} chukkers
                                  </Typography>
                                </Box>
                              </Stack>
                            }
                          />

                          {/* Chukker slider for games/tournaments */}
                          {isSelected && !isLesson && (
                            <Box sx={{ pl: 5, pr: 2 }}>
                              <Typography 
                                variant="body2" 
                                gutterBottom
                                sx={{ color: 'white' }}
                              >
                                Chukkers: {currentChukkers}
                              </Typography>
                              <Slider
                                value={currentChukkers}
                                onChange={(_, value) =>
                                  handleChukkerChange(horse.id, value as number)
                                }
                                min={1}
                                max={maxForHorse}
                                step={1}
                                marks
                                size="small"
                                valueLabelDisplay="auto"
                                disabled={maxForHorse <= 1}
                                sx={{
                                  color: 'white',
                                  '& .MuiSlider-markLabel': { color: 'rgba(255,255,255,0.7)' },
                                  '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.3)' },
                                }}
                              />
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
              })}
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default OwnHorseSelector;

